import React, { useContext, useEffect, useState } from "react";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { Pencil, RotateCcw } from "lucide-react";
import useAuthApi from "../../services/LandingPage/useAuthApi";
import Invoice from "./BuyInvoice";
import LoadingScreen from "../../utils/LoadingScreen";
import { GlobalContext } from "../../Context/GlobalContext";

const ProfileDashboard = ({ profiles }) => {
  const { bumppyApi } = useContext(GlobalContext);
  const {
    fetchOrganizationDetails,
    fetchInvoices,
    getWhatsappCredentials,
    updateOrganizationInfo,
    cancelSubscription,
  } = useProfileDashboard();

  const { fetchSubscriptionDetails } = useAuthApi();

  const [showModal, setShowModal] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [includeGST, setIncludeGST] = useState(false);
  const [gst, setGst] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [paymentGateway, setPaymentGateway] = useState("RAZORPAY");
  const [imageLink, setImageLink] = useState("");
  const [userID, setUserID] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [userData, setUserData] = useState({});
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
const [selectedImageType, setSelectedImageType] = useState("IMG");

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");

    if (!storedUser) return;

    const parsed = JSON.parse(storedUser);
    setUserID(parsed.userid);

    loadData(parsed.userid);
  }, []);

  const loadData = async (userid) => {
    try {
      setLoading(true);

      const sub = await fetchSubscriptionDetails(userid);
      if (sub.responseCode === "00") setSubscriptionData(sub.data);

      const org = await fetchOrganizationDetails(userid);
      if (org.responseCode === "00") setOrganizationDetails(org.response);

      const inv = await fetchInvoices(userid);
      if (inv.responseCode === "00") setInvoiceData(inv.data);

      const creds = await getWhatsappCredentials(userid);
      if (creds.responseCode === "00") setUserData(creds.response);
    } catch (err) {
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";

    const d = new Date(date);

    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

 const handleLogoUpload = (event) => {
   const file = event.target.files?.[0];

   if (!file) return;

   if (!file.type?.startsWith("image/")) {
     toast.error("Please select a valid image file");
     event.target.value = "";
     return;
   }

   if (file.size > 2 * 1024 * 1024) {
     toast.error("Image size must be under 2MB");
     event.target.value = "";
     return;
   }

   setSelectedImageType(file.type || "IMG");

   const reader = new FileReader();

   reader.onloadend = () => {
     setImageLink(reader.result);
   };

   reader.onerror = () => {
     toast.error("Unable to read selected image");
   };

   reader.readAsDataURL(file);
 };

  const uploadItemImage = async ({ item_name, image_data, type = "IMG" }) => {
    try {
      if (!bumppyApi) {
        throw new Error("Bumppy API instance missing");
      }

      if (!userID) {
        throw new Error("User ID is missing");
      }

      const res = await bumppyApi.post("/v1/uploadItemImagev2.php", {
        userid: String(userID),
        item_name,
        type,
        image_data,
      });

      if (String(res?.data?.status) === "0" && res?.data?.image_url) {
        return res.data;
      }

      throw new Error(
        res?.data?.message ||
          res?.data?.responseMessage ||
          "Image upload failed",
      );
    } catch (error) {
      console.error("Image upload failed:", error?.response?.data || error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Image upload failed",
      );

      return null;
    }
  };

  const getUploadedImageUrl = (uploadResponse) => {
    if (!uploadResponse) return "";

    const responseData = uploadResponse?.response;

    return (
      uploadResponse?.image_url ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      uploadResponse?.data?.image_url ||
      uploadResponse?.data?.imageUrl ||
      uploadResponse?.data?.url ||
      responseData?.image_url ||
      responseData?.imageUrl ||
      responseData?.url ||
      (typeof responseData === "string" ? responseData : "")
    );
  };

  const openEditModal = () => {
    if (!organizationDetails) return;

    setOrganizationName(organizationDetails.organization_name || "");
    setGstNumber(organizationDetails.gst || "");
    setPaymentGateway(organizationDetails.payment_gateway || "RAZORPAY");
    setImageLink(organizationDetails.brand_image || "");

    // GST toggle
    if (organizationDetails.gst) {
      setIncludeGST(true);
    } else {
      setIncludeGST(false);
    }

    setShowModal(true);
  };

  const openProvideInfoModal = () => {
    setOrganizationName("");
    setIncludeGST(false);
    setGst("");
    setGstNumber("");
    setPaymentGateway("RAZORPAY");
    setImageLink("");
    setShowModal(true);
  };

  const handleSaveChanges = async () => {
    if (!userID) {
      toast.error("User ID is missing");
      return;
    }

    if (!organizationName.trim()) {
      toast.error("Business name is required");
      return;
    }

    try {
      setIsSaving(true);

      let finalImageUrl = imageLink;

      // A newly selected file is stored as a data URL. Upload it first.
     if (imageLink?.startsWith("data:image/")) {
       const uploadResponse = await uploadItemImage({
         item_name: "organization_image",
         image_data: imageLink,
         type: selectedImageType,
       });

       if (!uploadResponse) {
         return;
       }

       finalImageUrl = getUploadedImageUrl(uploadResponse);

       if (!finalImageUrl) {
         console.error("Image URL missing in upload response:", uploadResponse);

         toast.error("Image uploaded, but image URL was not received");

         return;
       }
     }

      const payload = {
        userid: userID,
        organizationName: organizationName.trim(),
        gst: gstNumber,
        brand_image: finalImageUrl,
        payment_gateway: paymentGateway,
      };

      const res = await updateOrganizationInfo(payload);

      if (res?.responseCode === "00") {
        toast.success("Organization updated successfully");

        const org = await fetchOrganizationDetails(userID);
        if (org?.responseCode === "00") {
          setOrganizationDetails(org.response);
        }

        setImageLink(finalImageUrl);
        setShowModal(false);
      } else {
        toast.error(res?.responseMessage || "Organization update failed");
      }
    } catch (err) {
      console.error("Organization update failed:", err);
      toast.error("Organization update failed");
    } finally {
      setIsSaving(false);
    }
  };

  const loadPlanDetails = () => {
    if (!subscriptionData) {
      toast.error("Subscription details not available");
      return;
    }

    setPlanDetails(subscriptionData);
    setShowPlanModal(true);
  };

  const handleCancelSubscription = async () => {
    const payload = {
      userid: userID,
    };

    const res = await cancelSubscription(payload);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage);
      setShowPlanModal(false);
    }
  };

  return (
    <>
      <ToastContainer />

      <div className="p-6 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <div className="sticky top-0 z-30  pb-6">
          <div className="flex justify-between items-center mx-8 montserrat-font-1">
            <h1 className="text-2xl font-semibold">Profile</h1>

            <button
              onClick={() => loadData(userID)}
              className="border px-3 py-2 rounded-lg text-white bg-green-500 flex items-center gap-2 montserrat-font-1"
            >
              <RotateCcw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="flex justify-center py-10">
              <LoadingScreen text="Loading..." />
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6 montserrat-font-1">
            {/* PROFILE CARD */}
            <div className="bg-white rounded-xl shadow p-8 montserrat-font-1">
              {organizationDetails ? (
                <>
                  <div className="flex items-start justify-between mb-4 p-6 montserrat-font-1">
                    {/* LEFT SIDE */}
                    <div className="flex items-center gap-4">
                      {organizationDetails?.brand_image ? (
                        <img
                          src={organizationDetails.brand_image}
                          alt="logo"
                          className="w-24 h-24 rounded-lg bg-green-100 p-3 "
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-300 rounded"></div>
                      )}

                      <div>
                        <h2 className="text-3xl font-semibold">
                          {organizationDetails?.organization_name}
                        </h2>

                        <p className="text-md text-gray-500 max-w-[200px]">
                          Phone Number ID: {userData?.phone_no_id}
                        </p>
                      </div>
                    </div>

                    {/* RIGHT SIDE PEN ICON */}
                    <button
                      onClick={() => openEditModal()}
                      className="p-2 rounded-lg bg-blue-100 border "
                    >
                      <Pencil size={20} className="text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-4 montserrat-font-1">
                    <div>
                      <label className="text-sm text-gray-500">App ID</label>
                      <input
                        value={userData.app_id || ""}
                        disabled
                        className="w-full border rounded-lg p-2"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">
                        Login Email
                      </label>
                      <input
                        value={profiles?.email}
                        disabled
                        className="w-full border rounded-lg p-2"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">
                        Payment Gateway
                      </label>
                      <input
                        value={organizationDetails.payment_gateway || "N/A"}
                        disabled
                        className="w-full border rounded-lg p-2"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">
                        Business Name
                      </label>
                      <input
                        value={organizationDetails.organization_name || "N/A"}
                        disabled
                        className="w-full border rounded-lg p-2"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-10 montserrat-font-1">
                  <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 text-2xl font-bold">
                    {profiles?.username?.charAt(0)?.toUpperCase() || "P"}
                  </div>

                  <p className="text-gray-500 mb-4">Add Company Details</p>

                  <button
                    type="button"
                    onClick={openProvideInfoModal}
                    className="inline-flex items-center gap-3 px-5 py-2.5 border rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    <span className="w-9 h-9 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white font-semibold">
                      {profiles?.username?.charAt(0)?.toUpperCase() || "P"}
                    </span>

                    <span>Provide Information</span>
                  </button>
                </div>
              )}
            </div>

            {/* SUBSCRIPTION CARD */}
            <div className="space-y-6 montserrat-font-1">
              {/* CURRENT PLAN CONTAINER */}
              <div className="bg-white rounded-xl shadow p-6 montserrat-font-1">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">
                    {subscriptionData?.fk_plan_id}
                  </h2>

                  <Link to="/Profile/Plans">
                    <button className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded">
                      Upgrade Plan
                    </button>
                  </Link>
                </div>

                <hr className="mb-4" />

                <div className="space-y-3 text-sm montserrat-font-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Billing Cycle</span>
                    <span>{subscriptionData?.billing_cycle}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Purchased On</span>
                    <span>
                      {formatDateTime(subscriptionData?.purchased_on)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Expires On</span>
                    <span>{formatDateTime(subscriptionData?.expired_on)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Remaining Days</span>
                    <span>{subscriptionData?.remaining_days}</span>
                  </div>
                </div>

                <div className="text-right mt-3 montserrat-font-1">
                  <button
                    onClick={loadPlanDetails}
                    className=" text-blue-500 px-4 py-2 rounded underline font-semibold"
                  >
                    View Details
                  </button>
                </div>
              </div>

              {/* SUBSCRIPTION HISTORY CONTAINER */}

              <div className="bg-white rounded-xl shadow p-6 montserrat-font-1 ">
                <h3 className="text-center text-lg font-semibold mb-6">
                  Subscription History
                </h3>

                {/* SCROLL AREA */}
                <div className="max-h-[330px] overflow-y-auto pr-2 montserrat-font-1">
                  <div className="grid md:grid-cols-3 gap-6">
                    {invoiceData
                      .slice()
                      .reverse()
                      .map((item, index) => (
                        <div key={index} className="border rounded-xl p-4">
                          <h4 className="font-semibold mb-1">
                            {item.plan_name}
                          </h4>

                          <p className="text-green-600 font-semibold text-lg">
                            ₹{item.plan_price}
                          </p>

                          <p className="text-gray-500 text-sm">
                            Purchased On : {formatDateTime(item.purchased_on)}
                          </p>

                          <div className="mt-3">
                            <Invoice item={item} />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Update Business information */}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4 montserrat-font-1">
            {/* Modal */}
            <div className="bg-white w-full max-w-lg rounded-xl shadow-xl flex flex-col max-h-[90vh] montserrat-font-1">
              {/* Header — fixed */}
              <div className="flex items-center justify-between border-b px-6 py-4 flex-shrink-0">
                <h2 className="text-lg font-semibold">
                  Update Business Information
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-black text-xl"
                >
                  ×
                </button>
              </div>

              {/* Body — scrollable */}
              <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 montserrat-font-1">
                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                {/* GST Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">Include GST:</span>
                  <button
                    onClick={() => setIncludeGST(true)}
                    className={`px-3 py-1 rounded-md border text-sm ${
                      includeGST
                        ? "bg-green-500 text-white border-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    YES
                  </button>
                  <button
                    onClick={() => setIncludeGST(false)}
                    className={`px-3 py-1 rounded-md border text-sm ${
                      !includeGST
                        ? "bg-red-500 text-white border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    NO
                  </button>
                </div>

                {/* GST Rate */}
                {includeGST && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      GST Rate
                    </label>
                    <input
                      type="number"
                      value={gst}
                      onChange={(e) => setGst(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                )}

                {/* GST Number */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    GST Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                {/* Payment Gateway */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Payment Gateway
                  </label>
                  <select
                    value={paymentGateway}
                    onChange={(e) => setPaymentGateway(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="RAZORPAY">Razorpay</option>
                    <option value="PAYPAL">Paypal</option>
                    <option value="BUMPPYPAY">Bumppy Pay</option>
                  </select>
                </div>

                {/* Business Logo */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Business Logo
                  </label>
                  <input
                    type="file"
                    onChange={handleLogoUpload}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Footer — fixed */}
              <div className="flex justify-end gap-3 border-t px-6 py-4 flex-shrink-0 montserrat-font-1">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* cencel Subscription */}

        {showPlanModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
            <div className="bg-white w-[520px] rounded-xl shadow-lg montserrat-font-1">
              {/* Header */}
              <div className="px-6 py-4 border-b montserrat-font-1">
                <h2 className="text-blue-600 font-semibold text-lg">
                  Current Plan Details
                </h2>
              </div>

              {/* Body */}
              <div className="p-6 text-sm montserrat-font-1">
                <div className="grid grid-cols-2 gap-y-4 montserrat-font-1">
                  <span className="font-medium text-gray-700">Plan Name</span>
                  <span className="text-gray-600">
                    {planDetails?.fk_plan_id}
                  </span>

                  <span className="font-medium text-gray-700">Start Date</span>
                  <span className="text-gray-600">
                    {formatDateTime(planDetails?.start_date)}
                  </span>

                  <span className="font-medium text-gray-700">End Date</span>
                  <span className="text-gray-600">
                    {formatDateTime(planDetails?.end_date)}
                  </span>

                  <span className="font-medium text-gray-700">
                    Billing Cycle
                  </span>
                  <span className="text-gray-600">
                    {planDetails?.billing_cycle}
                  </span>

                  <span className="font-medium text-gray-700">
                    Purchased On
                  </span>
                  <span className="text-gray-600">
                    {formatDateTime(planDetails?.purchased_on)}
                  </span>

                  <span className="font-medium text-gray-700">
                    Remaining Days
                  </span>
                  <span className="text-gray-600">
                    {planDetails?.remaining_days}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 border border-red-500 text-red-600 rounded-md hover:bg-red-50"
                >
                  Cancel Subscription
                </button>

                <button
                  onClick={() => setShowPlanModal(false)}
                  className="px-4 py-2 border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileDashboard;
